<template>
  <div>
    <CompaniesAnalysisLayout
      :not-available-type="modifiedNotAvailableType"
      :company-uuid="$route.params.company_uuid"
      :is-competitor-view="isCompetitorTab"
      :is-more-than-editor="showMoreThanEditorAuthority"
    >
      <!-- ヘッダー -->
      <template #header>
        <CompaniesAnalysisHeader>
          <template #leftForm>
            <div v-if="isCompetitorTab">
              <ul class="c-links">
                <li
                  v-for="(tab) in tabs"
                  :key="tab"
                  class="c-links__item-competitor"
                >
                  <strong
                    :class="{ 'is-current': tab === currentTab }"
                    @click="changeTab(tab)"
                  >
                    <p>{{ $t('pages.companies.analysis.keywords.tab.' + tab ) }}</p>
                  </strong>
                </li>
              </ul>
            </div>
            <div v-else>
              <ul class="c-links">
                <li
                  v-for="(tab) in tabs"
                  :key="tab"
                  class="c-links__item"
                >
                  <strong
                    :class="{ 'is-current': tab === currentTab }"
                    @click="changeTab(tab)"
                  >
                    <p>{{ $t('pages.companies.analysis.keywords.tab.' + tab ) }}</p>
                  </strong>
                </li>
              </ul>
            </div>
          </template>
          <template #rightForm>
            <div class="c-buttonFilter" @click="openModal">
              {{ $t('labels.filters') }}
              <div class="c-buttonFilter__icon">
                <img :src="iconFilterPath">
              </div>
            </div>
            <DatePicker
              v-if="period"
              :initial-dates="period"
              opens="left"
              @input="changePeriod"
            />
            <div v-if="isExportCsvTab && canExportAuthority('analysis/keywords') && providedFeaturesCsvExport">
              <KkButton
                class="csvButton"
                color="secondary"
                height="38px"
                :font-bold="false"
                :text="$t('labels.csvDownload')"
                :disabled="loading"
                @click.prevent="setExportCsv"
              />
            </div>
          </template>
        </CompaniesAnalysisHeader>
      </template>

      <!-- データ -->
      <template #list>
        <Loading v-if="loading" />
        <template v-else-if="isComparisonTab(currentTab)">
          <CompaniesAnalysisComparisonPage
            :comparison-keywords="comparisonKeywords"
          />
        </template>
        <template v-else>
          <!-- 指定ワード -->
          <CompaniesAnalysisKeywordsList
            :items="specifyKeywords"
            :is-competitor-keyword="isCompetitorTab"
            :title="$t('pages.companies.analysis.keywords.labels.specifiedKeyword.title')"
            :description="$t('pages.companies.analysis.keywords.labels.specifiedKeyword.description')"
            :type="'specifyKeyword'"
            :form-values="formValues"
          />

          <!-- 頻出ワード -->
          <CompaniesAnalysisKeywordsList
            :items="keywords"
            :is-competitor-keyword="isCompetitorTab"
            :title="$t('pages.companies.analysis.keywords.labels.frequentKeyword.title')"
            :description="$t('pages.companies.analysis.keywords.labels.frequentKeyword.description')"
            :type="'keyword'"
            :form-values="formValues"
          />
        </template>

        <KkFilterModal
          :is-visible="modal"
          :filter-items="filterItems"
          :form-values="formValues"
          :total-count="filteredCount"
          @close="modal = false"
          @submit="handleSubmit"
          @reset="reset"
          @change-check-box="handleChangeFilterModalCheckBox"
        />
      </template>
    </CompaniesAnalysisLayout>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import companiesNotAvailableTypeMixin from '../../../mixins/companiesNotAvailableTypeMixin'
import { i18n } from '../../../plugins/i18n'
import CompaniesAnalysisLayout from '../../../layouts/CompaniesAnalysisLayout'
// TODO グラフが実装されたら共通CompaniesAnalysisHeaderに切り替える（現状集計期間の切り替えボタンが不要なので別headerで実装中）
import CompaniesAnalysisHeader from '../../../components/CompaniesAnalysis/Keywords/Header'
import CompaniesAnalysisKeywordsList from '../../../components/CompaniesAnalysis/Keywords/List'
import CompaniesAnalysisComparisonPage from '../../../components/CompaniesAnalysis/Keywords/Comparison/Page'
import Loading from '../../../components/Loading'
import { KkFilterModal } from '../../../components/projects'
import { KkButton } from '../../../components/base'
import DatePicker from '../../../components/DatePicker'
import iconFilterPath from '../../../../../images/common/icon_filter.svg'
import cloneDeep from 'lodash.clonedeep'
import { addAttributes } from '../../../modules/filterModalConst'
import {actionEvent} from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'CompaniesAnalysisKeywords',
  components: {
    CompaniesAnalysisLayout,
    CompaniesAnalysisHeader,
    CompaniesAnalysisKeywordsList,
    CompaniesAnalysisComparisonPage,
    Loading,
    DatePicker,
    KkFilterModal,
    KkButton
  },
  mixins: [companiesNotAvailableTypeMixin],
  async beforeRouteUpdate (to, from, next) {
    next()
    if (from.query.tab !== to.query.tab || from.query.select !== to.query.select) {
      await this.readSearchAttributes()
      await this.setEvacuationInitialFormValues(to.query.tab)
      this.initFilterValues()
      this.createFilter()
      this.handleSubmit(to.query.tab, to.query.select)
    }
  },
  data() {
    return {
      modal: false,    // モーダルウィンドウ（絞り込み条件）を表示するかどうか
      per: 50,         // 表示件数
      iconFilterPath,  // 画像のパス
      filterItems: [],
      evacuationInitialFormValues: null
    }
  },
  computed: {
    ...mapState({
      keywords: state => state.companiesAnalysisKeywords.data.keywords,
      filteredCount: state => state.companiesAnalysisKeywords.filteredCount,
      specifyKeywords: state => state.companiesAnalysisKeywords.data.specifyKeywords,
      comparisonKeywords: state => state.companiesAnalysisKeywords.data.storeComparisonKeywords,
      keywordsLoading: state => state.companiesAnalysisKeywords.loading,
      searchAttributes: state => state.companiesSearchTagGroups.data,
      searchAttributesLoading: state => state.companiesSearchTagGroups.loading,
      formValues: state => state.companiesAnalysisKeywords.formValues,
      period: state => state.filters.period,
    }),
    ...mapGetters({
      providedFeaturesCompetitor: 'companiesCompany/providedFeaturesCompetitor',
      canExportAuthority: 'companiesAccessibleApi/canExportAuthority',
      providedFeaturesCsvExport: 'companiesCompany/providedFeaturesCsvExport',
      showMoreThanEditorAuthority: 'companiesAccessibleApi/showMoreThanEditorAuthority',
      inboundOption: 'companiesCompany/inboundOption',
    }),
    crossSearchField() {
      return null
    },
    pageType() {
      return null
    },
    modifiedNotAvailableType() {
      if (!this.isRequiredCompetitorTab && this.notAvailableType === 'emptyCompetitor') return null

      return this.notAvailableType
    },
    loading() {
      return this.keywordsLoading || this.searchAttributesLoading
    },
    companyUuid() {
      return this.$route.params.company_uuid
    },
    tabs() {
      if (this.providedFeaturesCompetitor) {
        return ['store', 'competitor', 'specifyKeywordsComparison', 'frequentKeywordsComparison']
      } else {
        return ['store']
      }
    },
    currentTab() {
      return this.$route.query.tab
    },
    currentSelect() {
      return this.$route.query.select
    },
    isCompetitorTab() {
      return this.currentTab === 'competitor'
    },
    isRequiredCompetitorTab() {
      return ['competitor', 'specifyKeywordsComparison', 'frequentKeywordsComparison'].includes(this.currentTab)
    },
    isExportCsvTab() {
      return this.currentTab === 'store' || this.currentTab === 'competitor'
    },
    aggsQuery() {
      const formValues = {
        term: {
          start_date: moment(this.period.startDate).format(i18n.t('format.date.default')),
          end_date: moment(this.period.endDate).format(i18n.t('format.date.default'))
        },
        per: this.per,
        locales: this.formValues.locales.length === 0 ? this.evacuationInitialFormValues.locales : this.formValues.locales,
        brand_ids: this.formValues.brands.length === 0 ? this.evacuationInitialFormValues.brands : this.formValues.brands,
        site_types: this.formValues.sites.length === 0 ? this.evacuationInitialFormValues.sites : this.formValues.sites,
        franchisee_ids: this.formValues.franchisees.length === 0 ? this.evacuationInitialFormValues.franchisees : this.formValues.franchisees,
        user_company_ids: this.formValues.user_companies.length === 0 ? this.evacuationInitialFormValues.user_companies : this.formValues.user_companies,
        area_ids: this.formValues.areas.length === 0 ? this.evacuationInitialFormValues.areas : this.formValues.areas,
        prefectures: this.formValues.prefectures.length === 0 ? this.evacuationInitialFormValues.prefectures : this.formValues.prefectures,
        keyword_analysis_targets: this.formValues.keyword_analysis_targets.length === 0 ? this.evacuationInitialFormValues.keyword_analysis_targets : this.formValues.keyword_analysis_targets,
        original_tag_group_a_tag_ids: this.formValues.original_tag_group_a.length === 0 ? this.evacuationInitialFormValues.original_tag_group_a : this.formValues.original_tag_group_a,
        original_tag_group_b_tag_ids: this.formValues.original_tag_group_b.length === 0 ? this.evacuationInitialFormValues.original_tag_group_b : this.formValues.original_tag_group_b,
        original_tag_group_c_tag_ids: this.formValues.original_tag_group_c.length === 0 ? this.evacuationInitialFormValues.original_tag_group_c : this.formValues.original_tag_group_c,
        original_tag_group_d_tag_ids: this.formValues.original_tag_group_d.length === 0 ? this.evacuationInitialFormValues.original_tag_group_d : this.formValues.original_tag_group_d,
        original_tag_group_e_tag_ids: this.formValues.original_tag_group_e.length === 0 ? this.evacuationInitialFormValues.original_tag_group_e : this.formValues.original_tag_group_e,
      }

      if(this.currentTab === 'competitor') {
        formValues['competitor_brand_ids'] = this.formValues.competitor_brands.length === 0 ? this.evacuationInitialFormValues.competitor_brands : this.formValues.competitor_brands
      }

      return {
        query:{
          biz_companies_analysis_keyword_form: formValues
        }
      }
    },
    disableFilterItemTypes() {
      return this.isRequiredCompetitorTab ? ['emails', 'ratings', 'replies', 'comments', 'keyword_analysis_targets', 'topic_type'] : ['competitors', 'competitor_brands', 'emails', 'ratings', 'replies', 'comments', 'topic_type']
    }
  },
  async created() {
    await this.readSearchAttributes()
    await this.setEvacuationInitialFormValues()
    this.initFilterValues()
    this.createFilter()
    this.handleSubmit()
  },
  methods: {
    ...mapActions({
      readAnalysisKeywords: 'companiesAnalysisKeywords/readAnalysisKeywords',
      setFormValues: 'companiesAnalysisKeywords/setFormValues',
      readAnalysisKeywordsForFilter: 'companiesAnalysisKeywords/readAnalysisKeywordsForFilter',
      readAnalysisComparisonKeywordsForFilter: 'companiesAnalysisKeywords/readAnalysisComparisonKeywordsForFilter',
      readSearchAttributes: 'companiesSearchTagGroups/readSearchTagGroups',
      readAnalysisComparisonKeywords: 'companiesAnalysisKeywords/readAnalysisComparisonKeywords',
      setPeriod: 'filters/setPeriod',
      exportCsv: 'companiesAnalysisKeywords/exportCsv',
    }),
    params(tab = this.currentTab, select = this.currentSelect) {
      const params = {
        ...this.aggsQuery,
        companyUuid: this.companyUuid,
      }
      params.query.biz_companies_analysis_keyword_form.store_uuids = this.formValues.stores.length === 0 ? this.evacuationInitialFormValues.stores : this.formValues.stores
      if(this.isComparisonTab(tab)) {
        params.query.biz_companies_analysis_keyword_form.keywords_only = tab === 'specifyKeywordsComparison',
        params.query.biz_companies_analysis_keyword_form.brand_aggs = select === 'brandComparison'
        params.query.biz_companies_analysis_keyword_form.competitor_ids = this.formValues.competitors.length === 0 ? this.evacuationInitialFormValues.competitors : this.formValues.competitors
      }
      else if (tab === 'competitor') {
        params.query.biz_companies_analysis_keyword_form.competitor_ids = this.formValues.competitors.length === 0 ? this.evacuationInitialFormValues.competitors : this.formValues.competitors
      }
      return params
    },
    handleChangeFilterModalCheckBox({ kind, id }) {
      const newFormValues = Object.assign({}, this.formValues)

      if(newFormValues[kind].includes(id)) {
        newFormValues[kind] = newFormValues[kind].filter((_id) => _id !== id)
      } else {
        newFormValues[kind].push(id)
      }

      this.setFormValues(newFormValues)

      this.isComparisonTab(this.currentTab)
        ? this.readAnalysisComparisonKeywordsForFilter(this.params().query)
        : this.readAnalysisKeywordsForFilter(this.params().query)
    },
    /**
     * モーダルウィンドウ（絞り込み条件）を開く
     * @return { Void }
     */
    openModal() {
      if (this.loading) return
      this.modal = true
    },
    /**
     * モーダルウィンドウ（絞り込み条件）を閉じる
     * @return { Void }
     */
    closeModal() {
      this.modal = false
    },
    /**
     * 現在のタブが「指定ワード比較」「頻出ワード比較」かどうか
     * @param  { String } tab
     * @return { Boolean }
     */
    isComparisonTab(tab) {
      return ['specifyKeywordsComparison', 'frequentKeywordsComparison'].includes(tab)
    },
    /**
     * 期間の変更
     * @param  { Object } value
     * @return { Void }
     */
    changePeriod(value) {
      this.setPeriod(value)
      this.handleSubmit()
    },
    async setEvacuationInitialFormValues(tab = this.currentTab) {
      const formValues = {
        locales: this.searchAttributes.locales.tags.map(v => v.id),
        stores: this.searchAttributes.stores.tags.map(v => v.id),
        competitors: this.searchAttributes.competitors.tags.map(v => v.id),
        sites: this.searchAttributes.sites.tags.map(v => v.id),
        brands: this.searchAttributes.brands.tags.map(v => v.id),
        competitor_brands: this.searchAttributes.competitor_brands.tags.map(brand => brand.id),
        franchisees: this.searchAttributes.franchisees.tags.map(v => v.id),
        areas: this.searchAttributes.areas.tags.map(v => v.id),
        user_companies: this.searchAttributes.user_companies.tags.map(v => v.id),
        prefectures: this.searchAttributes.prefectures.tags.map(v => v.id),
        original_tag_group_a: this.searchAttributes.original_tag_group_a?.tags.map(v => v.id),
        original_tag_group_b: this.searchAttributes.original_tag_group_b?.tags.map(v => v.id),
        original_tag_group_c: this.searchAttributes.original_tag_group_c?.tags.map(v => v.id),
        original_tag_group_d: this.searchAttributes.original_tag_group_d?.tags.map(v => v.id),
        original_tag_group_e: this.searchAttributes.original_tag_group_e?.tags.map(v => v.id),
      }
      if (tab  === 'store') formValues['keyword_analysis_targets'] = this.searchAttributes.keyword_analysis_targets.tags.map(v => v.value)

      this.evacuationInitialFormValues = formValues
    },
    /**
     * 絞り込み条件の初期値をセット
     * @return { Void }
     */
    initFilterValues() {
      const formValues = {
        locales: [],
        stores: [],
        sites: [],
        brands: [],
        franchisees: [],
        areas: [],
        user_companies: [],
        prefectures: [],
        original_tag_group_a: [],
        original_tag_group_b: [],
        original_tag_group_c: [],
        original_tag_group_d: [],
        original_tag_group_e: [],
        keyword_analysis_targets: []
      }

      if(this.currentTab === 'competitor' || this.isComparisonTab(this.currentTab)) {
        formValues['competitors'] = []
        formValues['competitor_brands'] = []
      }

      this.setFormValues(formValues)
    },
    reset() {
      this.initFilterValues()
      this.handleSubmit()
    },
    /**
     * タブの変更
     * @param  { String } tab
     * @return { Void }
     */
    changeTab(tab) {
      if (this.loading) return

      if(this.isComparisonTab(tab)) {
        this.$router.push({ query: { tab: tab, select: 'storeComparison' } })
      } else {
        this.$router.push({ query: { tab: tab } })
      }
    },
    /**
     * 絞り込み条件のリセット
     * @return { Void }
     */
    handleReset() {
      this.initFilterValues()
      this.handleSubmit()
    },
    /**
     * 絞り込み条件の変更
     * @param  { String } tab
     * @param  { String } select
     * @return { Void }
     */
    handleSubmit(tab = this.currentTab, select = this.currentSelect) {
      // 「指定ワード比較」「頻出ワード比較」タブのデータ取得
      if(this.isComparisonTab(tab)) {
        this.readAnalysisComparisonKeywords(this.params(tab, select))
      }
      // 「自社」「競合」タブのデータ取得
      else {
        this.readAnalysisKeywords(this.params(tab, select))
      }
      this.closeModal()
    },
    /**
     * CSVエクスポート
     * @return { Void }
     */
    async setExportCsv() {
      const baseQuery = cloneDeep(this.aggsQuery)
      const params = {
        ...baseQuery,
        companyUuid: this.companyUuid,
      }
      // 「自社」「競合」タブのデータ取得
      params.query.biz_companies_analysis_keyword_form.store_uuids = this.formValues.stores.length === 0 ? this.evacuationInitialFormValues.stores : this.formValues.stores
      if (this.currentTab === 'competitor') {
        params.query.biz_companies_analysis_keyword_form.competitor_ids = this.formValues.competitors.length === 0 ? this.evacuationInitialFormValues.competitors : this.formValues.competitors
      }
      const isSuccess = await await this.exportCsv(params)
      if (!isSuccess) return
      this.$router.push({ name: 'CompaniesExportResults' })
      actionEvent('CsvDownload')
    },
    createFilter() {
      let attributes = []

      // インバウンドオプションが有効な場合、言語を追加
      if (this.inboundOption) attributes.push(this.searchAttributes.locales)

      if(this.currentTab === 'store') attributes.push(this.searchAttributes.keyword_analysis_targets)

      attributes = attributes.concat([
        this.searchAttributes.sites,
        this.searchAttributes.brands
      ])

      if(this.currentTab === 'competitor' || this.isComparisonTab(this.currentTab)) attributes.push(this.searchAttributes.competitor_brands)

      attributes = attributes.concat([
        this.searchAttributes.areas,
        this.searchAttributes.franchisees,
        this.searchAttributes.user_companies,
      ])

      if(this.searchAttributes.original_tag_group_a) attributes.push(this.searchAttributes.original_tag_group_a)
      if(this.searchAttributes.original_tag_group_b) attributes.push(this.searchAttributes.original_tag_group_b)
      if(this.searchAttributes.original_tag_group_c) attributes.push(this.searchAttributes.original_tag_group_c)
      if(this.searchAttributes.original_tag_group_d) attributes.push(this.searchAttributes.original_tag_group_d)
      if(this.searchAttributes.original_tag_group_e) attributes.push(this.searchAttributes.original_tag_group_e)

      attributes.push(this.searchAttributes.prefectures)
      attributes.push(this.searchAttributes.stores)
      if(this.currentTab === 'competitor' || this.isComparisonTab(this.currentTab)) attributes.push(this.searchAttributes.competitors)

      this.filterItems = attributes.map((attr) => {
        if(attr && attr.kind){
          return addAttributes(attr, attr.kind)
        }
      })
    }
  },
}
</script>
<style lang="scss" scoped>
.csvButton {
  margin-left: 10px;
}
</style>
